import { SessionHoldPanel } from '@/components/charging/SessionHoldPanel';
import { useCallback, useState, useEffect, useLayoutEffect, useMemo, useContext, useRef, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useStreamAwareInterval } from '@/store/sessionStream';
import { useTheme } from '@evi/skins';
import { ChargingAnimation } from '@evi/ui';
import {
  fetchActiveSkin, fetchPublicCharger,
  stopDriverSession, cancelDriverChargerSession,
} from '@/lib/api';
import { ACTIVE_SESSION_KEY, useActiveSessionRows } from '@/hooks/useActiveSession';
import { useSessionEndRedirect } from '@/hooks/useSessionEndRedirect';
import { gameForSkinOrTheme } from '@/lib/themeGames';
import { formatMoney } from '@/lib/money';
import { deriveGameAlert, type GameAlert } from '@/lib/chargingGameAlerts';
import { ChargingGameOverlay } from '@/components/charging/ChargingGameOverlay';
import { AutochargeOfferCard } from '@/components/charging/AutochargeOfferCard';
import { IdleBanner } from '@/components/charging/IdleBanner';
import { FobOfferCard, FobFinishLink } from '@/components/charging/FobOfferCard';
import { StatCard, StatusPill } from '@/components/primitives';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DriverAssistFab } from '@/components/DriverAssistSheet';
import { ExitOverlayContext } from '@/components/AppShell';
import { useEvieChatStore } from '@/store/evieChat';
import { toast } from '@/store/toast';
import styles from './ChargingPage.module.css';

interface Props {
  onNavigate: (path: string) => void;
  sessionId: string;
  /**
   * Whether THIS page follows a charger-side end to the receipt. True
   * at /charging/* (nothing else is mounted); HomeRoute passes false
   * and runs the redirect itself, because the home swaps this page out
   * for the map the moment the rows empty.
   */
  followEnd?: boolean;
  /** Fired with true the moment the page starts leaving on its own
   *  (Stop / Cancel dispatched, navigation issued) so the owner of the
   *  end-redirect treats the coming row drop as expected; with false
   *  when that attempt failed and the page is staying. */
  onLeaving?: (leaving: boolean) => void;
}

// For the charging demo when no real session exists, we animate an idealized
// charge curve so the ring + stats look alive even in an empty database.
interface LiveMetrics {
  battery_pct: number;
  power_kw: number;
  session_minutes: number;
  kwh_added: number;
  cost_cents: number;
}

function demoMetrics(): LiveMetrics {
  const t = (Date.now() / 1000) % 600;
  const pct = Math.min(99.9, 12 + (t / 600) * 88);
  return {
    battery_pct: pct,
    power_kw: 140 - pct * 0.3,
    session_minutes: Math.round(t / 6),
    kwh_added: (t / 600) * 54,
    cost_cents: Math.round((t / 600) * 1620),
  };
}

// Battery capacity assumption (kWh) when the meter reports no SOC —
// the same heuristic MiniChargingBar uses, so the ring and the bar
// agree. (They used to disagree: the page ignored meter soc_percent.)
const BATTERY_KWH = 60;

/**
 * Wall-clock elapsed since `startedAt`, as m:ss (h:mm:ss past the
 * hour). The server's duration_minutes only moves on MeterValues, so
 * the live "Elapsed" cell and the game strip tick from the start
 * timestamp instead and read like a stopwatch.
 */
function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
}

/**
 * Floor for the animation square. Below this the GAME TIME pill and
 * the kWh% overlay, both anchored to its corners, collide.
 */
const RING_MIN = 220;
/** Widest the hero goes — a tablet does not need a 700px fluid sim. */
const RING_MAX = 420;
/** How far past the page's 16px side padding the hero reaches on each
 *  side: the square sits 8px from the screen edge, wider than the cards
 *  under it, the way the 340 hero did on a 360 phone. */
const RING_BLEED = 8;

/**
 * The animation is the hero: it spans the page's width (bleeding
 * RING_BLEED past the card edges, capped at RING_MAX) on every device.
 * Warren's ruling 2026-09-16, after the height-bound square came out at
 * 270px on his phone once the rate cell made the stat grid 2×2: the
 * animation is the thing people notice, so it takes the width and the
 * chrome below it is compacted (tighter gaps, compact stat cards) to
 * keep Stop at rest above the pill on the common phone. Where a
 * viewport is too short for that (an SE, or a notch iPhone in
 * standalone), Stop is in flow a short swipe below — never anchored,
 * never behind the home indicator.
 *
 * Re-measured only when the viewport WIDTH changes (rotation, split
 * screen). Height changes are ignored on purpose: a browser toolbar
 * collapsing on scroll changes innerHeight by ~60px, and a square that
 * re-sized (and a WebGL sim that re-initialised) on every scroll would
 * be worse than anything it fixes. Returns null until the first
 * measurement so the canvas mounts once, at its real size — each mount
 * initialises a WebGL simulation. Runs in a layout effect, so nothing
 * paints before it.
 */
function useFillSquare(enabled: boolean, pageRef: RefObject<HTMLDivElement | null>): number | null {
  const [size, setSize] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (!enabled) return;
    const page = pageRef.current;
    if (!page) return;
    const measure = () => {
      const ps = getComputedStyle(page);
      const px = (v: string) => parseFloat(v) || 0;
      const inner = page.clientWidth - px(ps.paddingLeft) - px(ps.paddingRight);
      setSize(Math.max(RING_MIN, Math.min(RING_MAX, Math.floor(inner + 2 * RING_BLEED))));
    };
    measure();
    let lastWidth = window.innerWidth;
    const onResize = () => {
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      measure();
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
    // The ref object is stable; the element behind it exists only while
    // the live branch is rendered, which is exactly when `enabled` flips.
  }, [enabled]);
  return size;
}

/** 1s ticker while `running`; returns Date.now() on each tick. */
function useNowTicker(running: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [running]);
  return now;
}

/**
 * The live session view. Rendered at /charging/* (the start flows land
 * on /charging/active?charger=<identity>) and, via HomeRoute in App.tsx,
 * at '/' whenever the driver has a session — the active session owns
 * the home screen (frictionless flow, piece 2).
 *
 * Everything is on-page; nothing sits behind a sheet and no control is
 * bottom-anchored: Stop / Cancel are in-flow directly under the numbers,
 * so the home indicator can never eat them (the bottom-sheet drag handle
 * this replaces was the fourth such control this month).
 *
 * Three live phases from ONE shared cache (ACTIVE_SESSION_KEY; polled at
 * 4s here, the ambient consumers stay at 15s):
 *   pending   — the plug-in window after the Start tap, before the
 *               charger's StartTransaction: "Plug in now" + Cancel.
 *   active    — ring, kWh / cost / rate / elapsed, Stop, power flow.
 *   finishing — StopTransaction landed but the plug is still in:
 *               a quiet "Charging complete" card with the receipt link.
 */
export function ChargingPage({ onNavigate, sessionId, followEnd = true, onLeaving }: Props) {
  const { t, i18n } = useTranslation('charging');
  const isDemo = sessionId === 'demo';
  const { theme, mode } = useTheme();

  // Skin-aware theme code: if a skin is applied, the charging animation
  // should be the skin's, not the underlying family's. Otherwise fall
  // through to the family id.
  const skinQ = useQuery({
    queryKey: ['unlocks', 'active-skin'],
    queryFn: fetchActiveSkin,
    staleTime: 60_000,
  });
  const themeCode = skinQ.data?.skin?.code ?? theme.id;

  // Shared live-session cache. 4s baseline here (60s while the SSE
  // stream is open — push covers state changes). Demo route never
  // reads it; the hook still mounts (rules of hooks) but its data is
  // ignored below.
  const activeQ = useActiveSessionRows(4_000);
  const rows = isDemo ? [] : (activeQ.data ?? []);
  // The row that is (or was just) drawing current: 'active', or the
  // finishing tail. Server order puts an active row first.
  const liveSession = rows.find((s) => s.status === 'active' || s.phase === 'finishing');
  // The plug-in shell, when the driver has tapped Start and the charger
  // has not answered with StartTransaction yet.
  const pendingSession = liveSession ? undefined : rows.find((s) => s.status === 'pending');
  const isFinishing = liveSession?.phase === 'finishing';
  // The session's own currency (item 3): CA$ at a Canadian home. The
  // running cost and the rate are both in it.
  const costCurrency = liveSession?.cost?.currency ?? pendingSession?.cost?.currency;
  const lang = i18n.language;

  const [demo, setDemo] = useState<LiveMetrics>(demoMetrics());

  // Plug & charge went live on this session (second sighting of a pending
  // credential). Server-side flag on the row, so it survives a reload; the
  // toast fires once per session id per mount.
  const activatedToastFor = useRef<string | null>(null);
  useEffect(() => {
    if (isDemo || !liveSession?.autocharge_candidate?.activated) return;
    if (activatedToastFor.current === liveSession._id) return;
    activatedToastFor.current = liveSession._id;
    toast(t('autocharge.activated'), { tone: 'accent' });
  }, [isDemo, liveSession?._id, liveSession?.autocharge_candidate?.activated, t]);
  // Same for a fob: this session was the second sighting that switched the
  // driver's pending self-registered fob on (F3, no-grant offers).
  const fobActivatedToastFor = useRef<string | null>(null);
  useEffect(() => {
    if (isDemo || !liveSession?.fob_candidate?.activated) return;
    if (fobActivatedToastFor.current === liveSession._id) return;
    fobActivatedToastFor.current = liveSession._id;
    toast(t('fobActivated'), { tone: 'accent' });
  }, [isDemo, liveSession?._id, liveSession?.fob_candidate?.activated, t]);
  useEffect(() => {
    if (!isDemo) return;
    const id = setInterval(() => setDemo(demoMetrics()), 1000);
    return () => clearInterval(id);
  }, [isDemo]);

  // Three render modes:
  //   1. isDemo → synthetic `demo` metrics, no polling, demo route
  //      only (/charging/demo). Reachable for marketing screenshots
  //      / internal demos by direct URL; never landed on by real
  //      session-start flows after the PreSessionPage fix.
  //   2. !isDemo && liveSession → real metrics from /sessions/active.
  //   3. !isDemo && !liveSession → "Plug in now" holding state, from
  //      the pending row when the cache has it, or from nothing at all
  //      in the seconds before the first fetch lands.
  const isWaiting = !isDemo && !liveSession;
  const lastMeter = liveSession?.meter_values?.at(-1);
  const m: LiveMetrics = liveSession
    ? {
        // SOC: the meter's own soc_percent when the charger reports one,
        // else the delivered-kWh estimate. Capped at 99.9 while charging.
        battery_pct: typeof lastMeter?.soc_percent === 'number'
          ? Math.max(0, Math.min(99.9, lastMeter.soc_percent))
          : Math.min(99.9, Math.max(0, (liveSession.energy_kwh / BATTERY_KWH) * 100)),
        power_kw: isFinishing ? 0 : (lastMeter?.power_kw ?? 0),
        session_minutes: liveSession.duration_minutes,
        kwh_added: liveSession.energy_kwh,
        cost_cents: liveSession.cost?.total ?? 0,
      }
    : demo;

  // Wall-clock elapsed. Ticks while there is something to time
  // (pending or active); frozen at the server's ended_at once finishing.
  const timing = Boolean(pendingSession) || (Boolean(liveSession) && !isFinishing);
  const now = useNowTicker(timing);
  const startedAtMs = liveSession
    ? Date.parse(liveSession.started_at)
    : pendingSession
      ? Date.parse(pendingSession.started_at)
      : NaN;
  const elapsedMs = Number.isFinite(startedAtMs)
    ? (isFinishing && liveSession?.ended_at ? Date.parse(liveSession.ended_at) : now) - startedAtMs
    : m.session_minutes * 60_000;
  const elapsedLabel = isDemo ? formatElapsed(m.session_minutes * 60_000) : formatElapsed(elapsedMs);

  // Customer-facing rate, display only (see SessionData.tariff_rate_cents).
  // Customer-facing rate, display only (see SessionData.tariff_rate_cents).
  // A 0 is a real rate — the owner or a whitelisted friend on the home's
  // 0-rate tariff — and reads "Free" (Warren's ruling 2026-09-15) rather
  // than hiding the cell; null (no tariff on the row) still hides it.
  const rateCents = isDemo ? 42 : (liveSession?.tariff_rate_cents ?? null);
  const rateLabel = rateCents === null
    ? null
    : rateCents > 0
      ? `${formatMoney(rateCents, costCurrency, lang)}${liveSession?.tariff_rate_type === 'per_hour' ? t('rate.perHour') : t('rate.perKwh')}`
      : t('rate.free');

  // ── Theme mini-game ────────────────────────────────────────────────
  // Skin-aware: the worn skin's game wins (Midnight → Steady Climbing,
  // Sunset → Volley — those skins compose over 'default', so theme.id
  // alone could never select them), falling back to the effective
  // family from @evi/skins. A skin with no game falls through to its
  // base family; no match yields null and no affordance renders.
  const game = gameForSkinOrTheme(skinQ.data?.skin?.code ?? null, theme.id);
  const [gameOpen, setGameOpen] = useState(false);

  // Charger connectivity, polled ONLY while the game is open. The
  // charging screen doesn't otherwise need it, and a driver looking at
  // the session UI can see trouble for themselves — it's the driver
  // behind a game who can't, so that is the only time the extra
  // request is worth making.
  // Live cards: charger events refetch it; 10s is the fallback poll.
  const chargerIdentity = liveSession?.charger_identity ?? null;
  const chargerRefreshMs = useStreamAwareInterval(10_000);
  const chargerQ = useQuery({
    queryKey: ['public-charger', chargerIdentity],
    queryFn: () => fetchPublicCharger(chargerIdentity!),
    enabled: gameOpen && Boolean(chargerIdentity),
    refetchInterval: chargerRefreshMs,
  });

  const gameAlert: GameAlert | null = gameOpen
    ? deriveGameAlert({
        sessionActive: Boolean(liveSession),
        status: liveSession?.status ?? null,
        needsForceStop: liveSession?.needs_force_stop ?? null,
        paymentStatus: liveSession?.payment_status ?? null,
        // `undefined` while the first charger read is in flight — the
        // derivation treats that as unknown, never as offline.
        chargerOnline: chargerQ.data?.is_online,
        // The SESSION's plug, not connectors[0]: on a two-plug unit the
        // first plug may be another driver's session, and a fault there
        // must not pause this driver's game (nor a fault here go unseen).
        connectorStatus: (() => {
          const plug = liveSession?.connector_id ?? pendingSession?.connector_id ?? null;
          const conns = chargerQ.data?.connectors ?? [];
          const mine = plug != null ? conns.find((c) => c.connector_id === plug) : undefined;
          return (mine ?? conns[0])?.status ?? null;
        })(),
      })
    : null;

  // Memoised on the VALUES, not rebuilt each render, so the overlay's
  // effect posts a `session` message only when a number changed. The
  // elapsed string ticks once a second while the game is open, which is
  // the cadence the in-game stopwatch wants.
  const gameSession = useMemo(() => ({
    kwh: Number(m.kwh_added.toFixed(2)),
    cost: formatMoney(m.cost_cents, costCurrency, lang),
    elapsed: elapsedLabel,
  }), [liveSession?._id, m.kwh_added, m.cost_cents, elapsedLabel, costCurrency, lang]);

  const queryClient = useQueryClient();

  // Active-session end: dispatch RemoteStopTransaction via the server
  // (POST /sessions/:id/stop), then navigate to the summary. No
  // confirmation prompt — the driver is in the live view and tapped
  // Stop deliberately. The request can take up to 35s (the OCPP
  // round-trip); the button reads "Stopping…" meanwhile.
  const [endingSession, setEndingSession] = useState(false);
  // "Leaving" latch: set the moment Stop / Cancel is dispatched or a
  // navigation is issued, so the end-redirect (ours at /charging/*, or
  // HomeRoute's at '/') treats the row drop that follows as expected.
  // Cleared again if the attempt fails and the page stays.
  const leavingRef = useRef(false);
  const onLeavingRef = useRef(onLeaving);
  onLeavingRef.current = onLeaving;
  const setLeaving = useCallback((leaving: boolean) => {
    leavingRef.current = leaving;
    onLeavingRef.current?.(leaving);
  }, []);
  const markLeaving = useCallback(() => setLeaving(true), [setLeaving]);
  // Driver AI assistant — inline launcher for the global AppShell
  // sheet. Active-charging is the highest-stakes failure window: the
  // driver is committed, the car is plugged, current is (or isn't)
  // flowing. Session context binds via the effect below.
  const evieOpen = useEvieChatStore((s) => s.open);
  const openEvie = useEvieChatStore((s) => s.openSheet);
  const setEvieContext = useEvieChatStore((s) => s.setContext);
  const clearEvieContext = useEvieChatStore((s) => s.clearContext);
  // Stand down inside the exit-overlay clone (see PreSessionPage — the
  // clone's effects would otherwise clobber the live page's binding).
  const inExitOverlay = useContext(ExitOverlayContext);
  // Bind the REAL session id once the cache has it; the route param is
  // the 'active' sentinel on the start flows and absent at '/'.
  const evieSessionId = liveSession?._id ?? pendingSession?._id ?? sessionId;
  useEffect(() => {
    if (inExitOverlay) return;
    setEvieContext({
      surface: 'charge-start',
      sessionId: evieSessionId,
      supportPath: `/support?${new URLSearchParams({ session: evieSessionId }).toString()}`,
    });
    return () => clearEvieContext();
  }, [evieSessionId, setEvieContext, clearEvieContext, inExitOverlay]);

  const handleEnd = useCallback(async () => {
    if (isDemo) {
      onNavigate('/sessions/demo/complete');
      return;
    }
    if (!liveSession) return;
    if (endingSession) return;
    setEndingSession(true);
    markLeaving();
    try {
      await stopDriverSession(liveSession._id);
      // Invalidate the shared cache so no surface briefly re-renders
      // the active view post-stop from stale data. Navigation happens
      // after the OCPP dispatch succeeds (the charger's StopTransaction
      // echo finalizes status='completed' asynchronously; the summary
      // page is OK with either active-still-finalizing or completed).
      queryClient.invalidateQueries({ queryKey: ACTIVE_SESSION_KEY });
      onNavigate(`/sessions/${liveSession._id}/complete`);
    } catch (err) {
      toast(
        err instanceof Error ? err.message : t('toast.endError'),
        { tone: 'error' },
      );
      // Stay on the page so the user can retry. The charger may
      // still be running — they need to know the end attempt
      // failed, not silently lose state. The toast renders below the
      // game overlay (z 1300), so a Stop from the game strip that
      // fails closes the game — the driver must see the failure.
      setLeaving(false);
      setGameOpen(false);
      setEndingSession(false);
    }
  }, [isDemo, liveSession, endingSession, onNavigate, queryClient, t, markLeaving, setLeaving]);

  // Cancel handler for the "Plug in now" state. Three branches:
  //   - pending row in the cache: POST /sessions/:id/stop — the server
  //     cancels the Stripe pre-auth and marks it stopped_by_user (its
  //     existing pending branch). No confirm; nothing is flowing.
  //   - no row visible yet (the seconds before the first fetch, or a
  //     shell created without user_id): fall back to
  //     POST /chargers/:identity/driver-cancel using the ?charger=
  //     param the start flows pass.
  //   - the row went active under the driver's thumb: confirm before
  //     dispatching RemoteStop ("Stop charging now?").
  // After a successful cancel the driver goes home. On error, stay on
  // page with a toast — they need to know the charger may still run.
  const [cancelling, setCancelling] = useState(false);
  const [confirmStopOpen, setConfirmStopOpen] = useState(false);

  const dispatchStop = useCallback(async (): Promise<void> => {
    const row = liveSession ?? pendingSession;
    if (!row) {
      const charger = new URLSearchParams(window.location.search).get('charger');
      if (!charger) {
        // Defensive — the start flows pass ?charger=; if it's missing
        // we have nothing to look up server-side. Navigate away; the
        // pending sweeper cleans up any abandoned shell.
        markLeaving();
        onNavigate('/map');
        return;
      }
      markLeaving();
      await cancelDriverChargerSession(charger);
      queryClient.invalidateQueries({ queryKey: ACTIVE_SESSION_KEY });
      onNavigate('/map');
      return;
    }
    markLeaving();
    await stopDriverSession(row._id);
    queryClient.invalidateQueries({ queryKey: ACTIVE_SESSION_KEY });
    onNavigate('/map');
  }, [liveSession, pendingSession, onNavigate, queryClient, markLeaving]);

  const handleCancel = useCallback(async () => {
    if (cancelling) return;
    if (liveSession?.status === 'active') {
      // Confirm before stopping a session that's actively drawing
      // current. The driver may have tapped Cancel without
      // noticing the charger started.
      setConfirmStopOpen(true);
      return;
    }
    setCancelling(true);
    try {
      await dispatchStop();
    } catch (err) {
      toast(
        err instanceof Error ? err.message : t('toast.cancelError'),
        { tone: 'error' },
      );
      setLeaving(false);
      setCancelling(false);
    }
  }, [cancelling, liveSession, dispatchStop, t, setLeaving]);

  const handleConfirmStop = useCallback(async () => {
    await dispatchStop();
    setConfirmStopOpen(false);
  }, [dispatchStop]);

  // When the session ends from the charger side (BMS full, unplugged,
  // an operator stop) while the driver is looking at this page, follow
  // it to the receipt instead of falling back into "Plug in now". A
  // pending shell that vanishes without us cancelling it (swept, or
  // refused by the charger) sends the driver home with a toast.
  useSessionEndRedirect({
    enabled: followEnd && !isDemo,
    rows: activeQ.data,
    onNavigate,
    suppressed: () => leavingRef.current,
    startFailedText: t('toast.startEnded'),
  });

  // Animation square: the page-wide hero (see useFillSquare). Stop is in
  // flow below the compacted stat grid — never bottom-anchored.
  const pageRef = useRef<HTMLDivElement | null>(null);
  const ringSize = useFillSquare(isDemo || (Boolean(liveSession) && !isFinishing), pageRef);

  // Waiting state: the plug-in window. Polls continue in the
  // background, so the moment OCPP StartTransaction promotes the
  // pending row the next render flips into the live branch.
  if (isWaiting) {
    // "Plug N" — the same word the station picker and pre-session page
    // used for this connector, so the three screens agree.
    const stall = pendingSession?.connector_id != null
      ? t('stall', { n: String(pendingSession.connector_id) })
      : null;
    return (
      <div className={styles.page}>
        <p role="status" aria-live="polite" className={styles.srStatus}>{t('starting')}</p>
        <header className={styles.header}>
          {(stall || pendingSession?.charger_label) && (
            <div className={styles.statusRow}>
              {stall && (
                <StatusPill tone="good" variant="soft" size="sm">
                  ● {stall}
                </StatusPill>
              )}
              {pendingSession?.charger_label && (
                <span className={styles.stallMeta}>{pendingSession.charger_label}</span>
              )}
            </div>
          )}
          <div className={styles.titleRow}>
            <h1 className={styles.title}>{t('starting')}</h1>
            <DriverAssistFab variant="inline" onClick={openEvie} hidden={confirmStopOpen || evieOpen} />
          </div>
        </header>
        <div className={styles.waitingCard}>
          <div className={styles.waitingSpinner} aria-hidden />
          <p className={styles.waitingTitle}>{t('waiting.title')}</p>
          <p className={styles.waitingSub}>{t('waiting.sub')}</p>
          {/* Wall-clock since the Start tap. The Two-Minute-Window
              countdown (the charger's own plug-in timeout) lands in
              this slot once the OCPP side reports it. */}
          {pendingSession && (
            <p className={styles.waitingElapsed} aria-live="off">
              {t('waiting.elapsed', { elapsed: elapsedLabel })}
            </p>
          )}
        </div>
        <button
          type="button"
          className={`${styles.inlineCta} ${styles.inlineCtaDanger}`}
          onClick={() => void handleCancel()}
          disabled={cancelling}
        >
          {cancelling ? t('cta.cancelling') : t('cta.cancel')}
        </button>
        {confirmStopOpen && (
          <ConfirmDialog
            title={t('confirmStop.title')}
            body={<span>{t('confirmStop.body')}</span>}
            confirmLabel={t('confirmStop.confirm')}
            cancelLabel={t('confirmStop.cancel')}
            confirmTone="danger"
            onConfirm={handleConfirmStop}
            onCancel={() => setConfirmStopOpen(false)}
          />
        )}
      </div>
    );
  }

  const isActive = isDemo || (Boolean(liveSession) && !isFinishing);

  // Session-kWh percentage overlay. Renders bottom-right of the
  // shader canvas on themes whose native animation doesn't already
  // surface a percent readout. Luce has its dial; the skin
  // animations (Midnight / Sunset / Bloom) have their own readouts;
  // the shader-rendered families (Initiative / Electric Light /
  // Dark Electric / Neon / Drip / Cartographic / Aurora / Cyber)
  // don't, so this overlay surfaces session progress for them.
  //
  // Target of 50 kWh is a mid-range full-charge approximation
  // (Nissan Leaf ~40, mid-range Tesla 50-60). Single-line tweak if
  // we want to retune; could become a per-charger / per-user value
  // later (max battery capacity from the vehicle, charger's
  // max_session_kwh, etc.).
  const SHOW_KWH_PERCENT_THEMES = new Set<string>([
    'default',         // INITIATIVE
    'cartographic',
    'neon',
    'electric-light',
    'dark-electric',
    'obsidian',        // Drip
    'aurora',
    'cyber',
  ]);
  const SESSION_TARGET_KWH = 50;
  const showKwhPercent = SHOW_KWH_PERCENT_THEMES.has(themeCode) && isActive;
  const kwhPercent = Math.min(100, Math.max(0, Math.round((m.kwh_added / SESSION_TARGET_KWH) * 100)));

  // Power Flow bar normalization. Three cases:
  //   - Demo route: keep 250 kW (V3 Supercharger constant) so the
  //     marketing-screenshot visual stays calibrated against the
  //     synthetic ~140 kW power curve in `demoMetrics()`.
  //   - Live with hydrated `connector_max_power_kw`: the SPECIFIC
  //     connector's rated max. Joined server-side in /sessions/active.
  //   - Live without (legacy data / charger mutation / direct-Mongo
  //     inserts predating the required:true constraint): explicit
  //     null, bar hides entirely. The kW readout still renders —
  //     honest about not-knowing the normalization rather than
  //     picking a misleading fallback.
  const maxKw: number | null = isDemo
    ? 250
    : (liveSession?.connector_max_power_kw ?? null);
  const barPct = maxKw !== null
    ? Math.max(0, Math.min(1, m.power_kw / maxKw))
    : null;

  // Stall + charger label read from the live session payload. Demo
  // mode keeps its own static labels. Either field hidden when
  // missing rather than showing fallback fake values.
  // The demo keeps its marketing canon ("STALL #07 · V4 SUPERCHARGER");
  // a real session names the connector the way the picker did: Plug N.
  const stallLabel = isDemo
    ? 'STALL #07'
    : liveSession?.connector_id != null
      ? t('stall', { n: String(liveSession.connector_id) })
      : null;
  const chargerLabel = isDemo
    ? 'V4 SUPERCHARGER'
    : liveSession?.charger_label || null;

  // ── Finishing tail ─────────────────────────────────────────────────
  // StopTransaction has landed (the bill is final) but the connector
  // still reports Finishing — the plug is in. Not a session any more:
  // a quiet completion card with the final numbers and the receipt,
  // no ring at 0 kW and no Stop for a session that already stopped.
  if (!isDemo && liveSession && isFinishing) {
    return (
      <div className={styles.page}>
        <p role="status" aria-live="polite" className={styles.srStatus}>{t('finishing.title')}</p>
        <header className={styles.header}>
          {(stallLabel || chargerLabel) && (
            <div className={styles.statusRow}>
              {stallLabel && (
                <StatusPill tone="good" variant="soft" size="sm">
                  ● {stallLabel}
                </StatusPill>
              )}
              {chargerLabel && <span className={styles.stallMeta}>{chargerLabel}</span>}
            </div>
          )}
          <div className={styles.titleRow}>
            <h1 className={styles.title}>{t('finishing.title')}</h1>
            <DriverAssistFab variant="inline" onClick={openEvie} hidden={evieOpen} />
          </div>
        </header>
        <div className={styles.finishingCard}>
          <SessionHoldPanel session={liveSession} />
          <IdleBanner session={liveSession} />
          <p className={styles.finishingLead}>{t('finishing.plugAttached')}</p>
          <p className={styles.finishingSub}>
            {t('finishing.sub', { location: liveSession.location_name ?? '' })}
          </p>
        </div>
        <div className={styles.statRow}>
          <StatCard label={t('stat.added')} value={m.kwh_added.toFixed(1)} unit="kWh" />
          <StatCard label={t('stat.cost')} value={formatMoney(m.cost_cents, costCurrency, lang)} />
          <StatCard label={t('stat.elapsed')} value={elapsedLabel} />
        </div>
        <button
          type="button"
          className={styles.inlineCta}
          onClick={() => onNavigate(`/sessions/${liveSession._id}/complete`)}
        >
          {t('finishing.receipt')}
        </button>
      </div>
    );
  }

  return (
    <div className={styles.page} ref={pageRef}>
      <SessionHoldPanel session={liveSession} />
      <IdleBanner session={liveSession} />
      <p role="status" aria-live="polite" className={styles.srStatus}>{t('title')}</p>
      <header className={styles.header}>
        {(stallLabel || chargerLabel) && (
          <div className={styles.statusRow}>
            {stallLabel && (
              <StatusPill tone="good" variant="soft" size="sm">
                ● {stallLabel}
              </StatusPill>
            )}
            {chargerLabel && (
              <span className={styles.stallMeta}>{chargerLabel}</span>
            )}
          </div>
        )}
        {/* "Charging" on the left, the EVII launcher on the right of the
            same row — sits above the animation square. */}
        <div className={styles.titleRow}>
          <h1 className={styles.title}>{t('title')}</h1>
          <DriverAssistFab
            variant="inline"
            onClick={openEvie}
            hidden={confirmStopOpen || evieOpen || isDemo}
          />
        </div>
      </header>

      <div className={styles.ringWrap}>
        {/* Theme-aware charging visual. Reads the active skin code (or
            falls through to the family id when no skin is applied). */}
        {/* canvasOverlayWrap anchors the GAME TIME pill (bottom-left)
            and the kWh-percent overlay (bottom-right) to the animation
            frame's corners. inline-block sizes to ChargingAnimation's
            outer frame. */}
        <div className={styles.canvasOverlayWrap}>
          {ringSize !== null && (
            <ChargingAnimation
              themeCode={themeCode}
              mode={mode}
              percent={m.battery_pct}
              powerKw={m.power_kw}
              // Real cumulative session kWh -> uKwhScale. Without this the
              // shader falls into the synthetic picker wind-up (off percent)
              // even during a live session; with it, the animation grows with
              // the energy actually delivered.
              kwhDelivered={m.kwh_added}
              active={isActive}
              size={ringSize}
            />
          )}
          {/* Play affordance — only when the active theme HAS a game.
              Anchored to the animation frame, bottom-left so it never
              collides with the kWh-percent overlay bottom-right. */}
          {game && (
            <button
              type="button"
              className={styles.gameFab}
              onClick={() => setGameOpen(true)}
              /* Accessible name LEADS with the visible label (so it satisfies
                 label-in-name for voice control) and then names the specific
                 game, which a screen-reader user would otherwise never hear. */
              aria-label={`${t('game.cta')}: ${t(game.labelKey)}`}
              title={t(game.labelKey)}
            >
              <span className={styles.gameFabIcon} aria-hidden>▶</span>
              <span className={styles.gameFabLabel}>{t('game.cta')}</span>
            </button>
          )}
          {showKwhPercent && (
            <span
              className={styles.kwhPercentOverlay}
              aria-label={t('a11y.kwhPercent', { percent: kwhPercent, target: SESSION_TARGET_KWH })}
            >
              {kwhPercent}%
            </span>
          )}
        </div>
      </div>

      {/* The four numbers the driver must never lose sight of: energy,
          cost so far, the rate it is priced at, and elapsed. Rate is
          omitted (three-up) when the session has no priced tariff. */}
      <div className={rateLabel ? styles.statRowFour : styles.statRow}>
        <StatCard label={t('stat.added')} value={m.kwh_added.toFixed(1)} unit="kWh" density="compact" />
        <StatCard label={t('stat.cost')} value={formatMoney(m.cost_cents, costCurrency, lang)} density="compact" />
        {rateLabel && <StatCard label={t('stat.rate')} value={rateLabel} density="compact" />}
        <StatCard label={t('stat.elapsed')} value={elapsedLabel} density="compact" />
      </div>

      {/* Stop — in-flow, directly under the numbers. Deliberately NOT a
          bottom-anchored sticky control: those get eaten by the home
          indicator (fourth time this month), and a Stop the driver
          cannot reach is the one control that must never fail. */}
      <button
        type="button"
        className={`${styles.inlineCta} ${styles.inlineCtaDanger}`}
        onClick={() => void handleEnd()}
        disabled={endingSession}
      >
        {endingSession ? t('cta.stopping') : t('cta.endSession')}
      </button>

      <div className={styles.flowCard}>
        <div className={styles.flowHeader}>
          <span className={styles.flowLabel}>{t('powerFlow')}</span>
          <span className={styles.flowValue}>
            {Math.round(m.power_kw)}
            <span className={styles.flowUnit}> kW</span>
          </span>
        </div>
        {/* Bar conditionally rendered. When the connector's
            max_power_kw is unresolvable (barPct === null), the
            kW readout above stands alone. */}
        {barPct !== null && (
          <div
            className={styles.flowTrack}
            style={{
              ['--flow-scale' as string]: barPct.toFixed(3),
            } as React.CSSProperties}
          >
            <span className={styles.flowFill} />
          </div>
        )}
      </div>

      {/* Plug & charge offer — the car announced its own id at the plug
          before this app start; offer to link it, once. Server-evaluated
          (autocharge_candidate on the row), server-dismissed, in flow
          like everything else on this page — never bottom-anchored. */}
      {!isDemo && liveSession?.autocharge_candidate?.present === true && (
        <AutochargeOfferCard
          sessionId={liveSession._id}
          pendingOnLink={liveSession.autocharge_candidate.pending_on_link === true}
        />
      )}

      {/* Tap-first fob offer (F3) — an unregistered fob was tapped on this
          plug just before this app start. Same shape and rules as the plug
          & charge offer: server-evaluated (fob_candidate), server-dismissed,
          in flow. Registering opens the ordinary paywall in Cards & fobs. */}
      {!isDemo && liveSession?.fob_candidate?.present === true && (
        <FobOfferCard sessionId={liveSession._id} onNavigate={onNavigate} />
      )}
      {/* Taken up but not finished (left the paywall to add a card, say):
          the server only sends window_id while that window still waits at
          the payment step, so this never points anywhere dead. */}
      {!isDemo && liveSession?.fob_candidate?.window_id && (
        <FobFinishLink windowId={liveSession.fob_candidate.window_id} onNavigate={onNavigate} />
      )}

      {/* Sponsor / partner CTA slot — RESERVED, NOT RENDERED. The
          .sponsorCard rules stay in the CSS so the same shell can drop
          back in once a real sponsor field lands on the payload. */}

      {/* Confirm-stop dialog — only mounted from the Cancel path
          when the session has already gone active (Cancel asked
          "Stop charging now?"). Not used by Stop, which is an
          explicit-intent control in the live view. */}
      {confirmStopOpen && (
        <ConfirmDialog
          title={t('confirmStop.title')}
          body={
            <span>
              {t('confirmStop.body')}
            </span>
          }
          confirmLabel={t('confirmStop.confirm')}
          cancelLabel={t('confirmStop.cancel')}
          confirmTone="danger"
          onConfirm={handleConfirmStop}
          onCancel={() => setConfirmStopOpen(false)}
        />
      )}

      {/* Mounted only while open, so the iframe is created on first play
          and destroyed on close — nothing runs behind the session UI.
          The overlay keeps the session numbers in a strip above the
          game (ruling: the numbers survive the choice to play). Stop is
          deliberately not in that strip — closing returns the driver
          here, where End Session has its confirm. */}
      {game && gameOpen && (
        <ChargingGameOverlay
          game={game}
          session={gameSession}
          alert={gameAlert}
          alertText={(a) => t(a.messageKey)}
          onClose={() => setGameOpen(false)}
          onResult={(result) => {
            // Leaderboard work lands later; until then the run is only
            // recorded, never silently dropped.
            // eslint-disable-next-line no-console
            console.info('[charging-game] result', { ns: game.ns, ...result });
          }}
          title={t(game.labelKey)}
          closeLabel={t('game.close')}
          strip={{
            items: [
              { label: t('stat.added'), value: `${m.kwh_added.toFixed(1)} kWh` },
              { label: t('stat.cost'), value: formatMoney(m.cost_cents, costCurrency, lang) },
              ...(rateLabel ? [{ label: t('stat.rate'), value: rateLabel }] : []),
              { label: t('stat.elapsed'), value: elapsedLabel },
            ],
          }}
        />
      )}
    </div>
  );
}
